import { type ReactNode } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import { showSuiErrorSnackbar, showSuiSuccessSnackbar } from 'components/SuiSnackbar';
import SoftTypography from 'components/SoftTypography';

import { localeToIntlCode, type Locale } from 'lib/i18n/config';
import { getTranslations } from 'lib/i18n/dictionary';
import { useLocale } from 'lib/i18n/client';
import { formatPlural } from 'site/plural';
import type { PluralForms } from 'lib/i18n/types';

export function useGalleryText() {
  const locale = useLocale();
  const t = getTranslations(locale).galleriesPage;
  return {
    locale,
    t,
    plural: (forms: PluralForms, count: number) => formatPlural(locale, forms, count),
  };
}

export function notifyGallerySuccess(title: string, message: string) {
  showSuiSuccessSnackbar(message, { title });
}

export function notifyGalleryError(title: string, message: string) {
  showSuiErrorSnackbar(message, { title });
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

// `YYYY-MM-DD` read as a local day, so no time zone moves it.
export function formatShootDate(date: string | null | undefined, locale: Locale): string | null {
  const match = date ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(date) : null;
  if (!match) {
    return null;
  }
  const [, year, month, day] = match.map(Number);
  return new Intl.DateTimeFormat(localeToIntlCode[locale], {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
}

export function formatDateTime(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(localeToIntlCode[locale], {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function sectionLabel(section: { name: string }, unnamed: string): string {
  return section.name.trim() || unnamed;
}

// Admin forms put the label above the input, as in the Services editor.
export function FieldLabel({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
      <SoftTypography component="label" variant="caption" fontWeight="bold" htmlFor={htmlFor}>
        {children}
      </SoftTypography>
    </SoftBox>
  );
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  busy?: boolean;
  testId: string;
  onCancel: () => void;
  onConfirm: () => void;
}

// Destructive confirmations: the confirm button is outlined red.
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  busy = false,
  testId,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      fullWidth
      maxWidth="xs"
      data-testid={testId}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <SoftTypography variant="button" fontWeight="regular" color="text">
          {body}
        </SoftTypography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <SoftButton variant="outlined" color="secondary" onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </SoftButton>
        <SoftButton
          variant="outlined"
          color="error"
          onClick={onConfirm}
          disabled={busy}
          data-testid={`${testId}-confirm`}
        >
          {confirmLabel}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}
